import { Injectable } from "@angular/core";
import { toEpoch } from "@firm360/libs/util/formatting";
import type { PendoProfile } from "../../pendo-profile.model";
import { AbstractServerProfileSource } from "./abstract-server.pendo-profile-source";

/**
 * The server's profile through its deprecated endpoint, which carries less. It
 * stands in when the current endpoint fails, including on backends that
 * predate it, so a backend rollback doesn't take analytics with it. Remove once
 * no such backend can be deployed.
 */
@Injectable()
export class LegacyServerProfileSource extends AbstractServerProfileSource<LegacyPendoProfileDTO> {
    protected readonly url = "api/pendo";

    protected translateResponse(dto: LegacyPendoProfileDTO): PendoProfile {
        if (!dto.visitorId || !dto.accountId) {
            throw new Error("Legacy Pendo response carries no ids");
        }
        return {
            visitor: {
                id: dto.visitorId,
                email: dto.visitorEmail ?? null,
                createdAt: toEpoch(dto.visitorCreatedDate),
                lastLoginAt: toEpoch(dto.visitorLastLogin),
                internal: dto.visitorIsInternal === "true",
            },
            account: {
                id: dto.accountId,
                name: dto.accountName ?? null,
                subdomain: dto.accountSubdomain ?? null,
                createdAt: toEpoch(dto.accountCreationDate),
                planTier: dto.accountFirmPlanTier ?? null,
                beta: toBoolean(dto.accountIsBeta),
                cancelledAt: toEpoch(dto.accountCancellationDate),
            },
        };
    }
}

/** Every value is a string, `"null"`/`null` for none, dates as epoch milliseconds. */
interface LegacyPendoProfileDTO {
    visitorId?: string | null;
    visitorEmail?: string | null;
    visitorCreatedDate?: string | null;
    visitorLastLogin?: string | null;
    visitorIsInternal?: string | null;
    accountId?: string | null;
    accountName?: string | null;
    accountCreationDate?: string | null;
    accountFirmPlanTier?: string | null;
    accountSubdomain?: string | null;
    accountIsBeta?: string | null;
    accountCancellationDate?: string | null;
}

function toBoolean(value: string | null | undefined): boolean | null {
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
}
